import { cn } from '@/lib/cn';
import { Card } from '@/ui/Card';
import { SkeletonTarjetas } from '@/ui/Skeleton';
import { EstadoError } from '@/components/EstadoError';
import { useCatalogo, type InformeDelCatalogo } from './api';
import { Informe } from './Informe';
import { Personalizado } from './Personalizado';

/**
 * Informes — `#informes`. Módulo 14, opción 1.
 *
 * Diez informes del portal, su vista previa y su descarga en Excel, más el
 * personalizado, que combina varios en un archivo.
 *
 * ## Lo que cambia
 *
 * - **Cada vista tiene su dirección**: `#informes/<clave>` y
 *   `#informes/personalizado`. El vanilla repintaba el contenedor sin tocar el
 *   hash, así que el Atrás del navegador salía del módulo y no se podía
 *   enlazar un informe concreto ni recargarlo.
 * - **Un fallo al cargar el catálogo ya no se lee como «Sin acceso».** Lo decía
 *   para cualquier error —un 500, una caída de red, un token vencido— y sin
 *   ofrecer reintentar. Ahora `EstadoError` distingue el 403 del resto.
 * - **La vista previa escapa los datos.** El vanilla los interpolaba en el
 *   marcado; son nombres, motivos de auditoría y respuestas de encuesta, todos
 *   escritos por personas. Lo cuenta `Informe.tsx`.
 */
export function Informes() {
  const sub = window._routeParams?.[0] ?? null;
  if (sub === 'personalizado') return <Personalizado />;
  if (sub) return <Informe clave={sub} />;
  return <CatalogoDeInformes />;
}

function CatalogoDeInformes() {
  const catalogo = useCatalogo();

  return (
    <>
      <header className="mb-5">
        <h1 className="flex items-center gap-2 text-6 font-bold text-brand-ink">
          <i className="bi bi-file-earmark-bar-graph" aria-hidden="true" /> Informes
        </h1>
        <p className="mt-1 text-3 text-muted">
          Genera y descarga información del portal en Excel.
        </p>
      </header>

      {catalogo.isPending ? (
        <SkeletonTarjetas n={6} />
      ) : catalogo.isError ? (
        <EstadoError
          err={catalogo.error}
          que="el catálogo de informes"
          onReintentar={() => void catalogo.refetch()}
        />
      ) : !catalogo.data ? null : (
        <>
          <TarjetaDeInforme
            href="#informes/personalizado"
            icono="bi-sliders2"
            titulo="Informe personalizado"
            descripcion="Combina varios informes en un solo archivo, y elige qué columnas lleva cada uno."
            destacada
          />
          <ul className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {catalogo.data.reports.map((r) => (
              <li key={r.key} className="flex">
                <TarjetaDeInforme
                  href={`#informes/${r.key}`}
                  icono={r.icon}
                  titulo={r.name}
                  descripcion={r.desc}
                />
              </li>
            ))}
          </ul>
        </>
      )}
    </>
  );
}

/** Un enlace con forma de tarjeta, no un `<button>` con `onclick`: así entra en
 *  el historial y se puede abrir en otra pestaña. */
function TarjetaDeInforme({
  href, icono, titulo, descripcion, destacada,
}: {
  href: string;
  icono: string;
  titulo: string;
  descripcion: string;
  destacada?: boolean;
}) {
  return (
    <Card
      interactiva
      className={cn('w-full', destacada && 'border-brand-ink/40 bg-brand/5')}
    >
      <a href={href} className="flex h-full items-center gap-3 p-4 focus-visible:focus-anillo">
        <span
          className={cn(
            'grid size-11 shrink-0 place-items-center rounded-md text-5',
            destacada ? 'bg-accent text-brand-ink' : 'bg-sunken text-brand-ink',
          )}
          aria-hidden="true"
        >
          <i className={cn('bi', icono)} />
        </span>
        <span className="min-w-0 flex-1">
          <strong className="block text-3 font-bold text-brand-ink">{titulo}</strong>
          <span className="mt-0.5 block text-2 text-muted">{descripcion}</span>
        </span>
        <i className="bi bi-chevron-right shrink-0 text-muted" aria-hidden="true" />
      </a>
    </Card>
  );
}

/** La cabecera con el enlace de vuelta, que comparten las dos vistas de dentro. */
export function CabeceraDeInforme({ icono, titulo }: { icono: string; titulo: string }) {
  return (
    <header className="mb-4 flex flex-wrap items-center gap-3">
      <a
        href="#informes"
        className="inline-flex items-center gap-1 text-3 font-semibold text-brand-ink hover:underline focus-visible:focus-anillo"
      >
        <i className="bi bi-arrow-left" aria-hidden="true" /> Informes
      </a>
      <h1 className="flex min-w-0 items-center gap-2 text-5 font-bold text-brand-ink">
        <i className={cn('bi', icono)} aria-hidden="true" /> {titulo}
      </h1>
    </header>
  );
}

/** El informe que pide la sub-ruta, o `undefined` si esa clave no existe. */
export function informeDe(
  reports: InformeDelCatalogo[] | undefined, clave: string,
): InformeDelCatalogo | undefined {
  return reports?.find((r) => r.key === clave);
}
